import { supabase } from '../supabaseClient';

/**
 * Onboarding Tour Service
 * 
 * Handles tour state persistence, user metadata updates, and tour lifecycle
 */

export class OnboardingService {
  /**
   * Check if user has completed onboarding tour
   * @param {string} userId - User ID from auth
   * @returns {Promise<boolean>}
   */
  static async hasTourCompleted(userId) {
    if (!userId) return false;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('onboarding_completed_at')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error checking tour status:', error);
        return false;
      }

      return !!data?.onboarding_completed_at;
    } catch (err) {
      console.error('Unexpected error checking tour status:', err);
      return false;
    }
  }

  /**
   * Mark tour as completed
   * @param {string} userId - User ID from auth
   * @param {string} userRole - 'lawyer' or 'client'
   * @returns {Promise<boolean>}
   */
  static async completeTour(userId, userRole = 'client') {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          onboarding_completed_at: new Date().toISOString(),
          onboarding_tour_version: 1,
          onboarding_skipped: false,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error marking tour as completed:', error);
        return false;
      }

      // Track event (optional - implement if you have analytics)
      this.trackEvent('tour_completed', {
        user_id: userId,
        user_role: userRole,
        timestamp: new Date().toISOString(),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error completing tour:', err);
      return false;
    }
  }

  /**
   * Mark tour as skipped
   * @param {string} userId - User ID from auth
   * @param {string} userRole - 'lawyer' or 'client'
   * @returns {Promise<boolean>}
   */
  static async skipTour(userId, userRole = 'client') {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          onboarding_completed_at: new Date().toISOString(),
          onboarding_skipped: true,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error marking tour as skipped:', error);
        return false;
      }

      // Track event
      this.trackEvent('tour_skipped', {
        user_id: userId,
        user_role: userRole,
        timestamp: new Date().toISOString(),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error skipping tour:', err);
      return false;
    }
  }

  /**
   * Reset tour completion status (allow user to restart)
   * @param {string} userId - User ID from auth
   * @returns {Promise<boolean>}
   */
  static async resetTour(userId) {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          onboarding_completed_at: null,
          onboarding_skipped: false,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error resetting tour:', error);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Unexpected error resetting tour:', err);
      return false;
    }
  }

  /**
   * Get tour progress (for analytics/debugging)
   * @param {string} userId - User ID from auth
   * @returns {Promise<object>}
   */
  static async getTourProgress(userId) {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('onboarding_completed_at, onboarding_skipped, onboarding_tour_version')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching tour progress:', error);
        return null;
      }

      return {
        completed: !!data?.onboarding_completed_at,
        completedAt: data?.onboarding_completed_at,
        skipped: data?.onboarding_skipped,
        version: data?.onboarding_tour_version || 1,
      };
    } catch (err) {
      console.error('Unexpected error fetching tour progress:', err);
      return null;
    }
  }

  /**
   * Track tour events for analytics
   * @param {string} eventName - Event name (tour_started, tour_completed, etc)
   * @param {object} metadata - Additional event metadata
   */
  static async trackEvent(eventName, metadata = {}) {
    try {
      // TODO: Implement your analytics tracking here
      // Examples:
      // - Supabase analytics table
      // - Mixpanel, Segment, Google Analytics
      // - Custom logging service

      console.debug(`[Tour Event] ${eventName}`, metadata);

      // Example: Save to Supabase analytics table (if you create one)
      /*
      const { error } = await supabase
        .from('tour_events')
        .insert({
          event_name: eventName,
          user_id: metadata.user_id,
          user_role: metadata.user_role,
          timestamp: metadata.timestamp,
          metadata: metadata,
        });

      if (error) {
        console.error('Error tracking event:', error);
      }
      */
    } catch (err) {
      console.error('Error in trackEvent:', err);
    }
  }

  /**
   * Get tour configuration
   * @returns {object}
   */
  static getConfig() {
    return {
      TRIGGER_DELAY_MS: 500,
      TOOLTIP_OFFSET_PX: 16,
      ANIMATION_DURATION_MS: 240,
      ENABLE_KEYBOARD_NAV: true,
      ALLOW_SKIP: true,
      AUTO_DISMISS_ON_RESIZE: false,
      LAWYER_TOUR_VERSION: 1,
      CLIENT_TOUR_VERSION: 1,
    };
  }
}

export default OnboardingService;
